<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <!-- Logo Section -->
        <div class="login-header">
          <img src="/logo.png" alt="美好假期" class="login-logo-img" />
        </div>

        <!-- Login Form -->
        <Card class="login-form-card">
          <template #header>
            <h2 class="login-title">登入系統</h2>
          </template>

          <!-- General Error Message -->
          <div v-if="route.query.access === 'unavailable'" class="error-alert" role="alert">目前無法確認此帳號的頁面權限。請重新登入以同步角色；若仍無法進入，請管理員確認帳號角色與啟用狀態。</div>
          <div v-if="route.query.password === 'updated'" class="password-complete" role="status">
            <i class="fas fa-check-circle" aria-hidden="true"></i>
            <span>完成密碼設定後，請使用您的 Email 與新密碼登入。</span>
          </div>
          <div v-if="errors.general" class="error-alert">
            <i class="fas fa-exclamation-triangle error-icon"></i>
            <span class="error-message">{{ errors.general }}</span>
          </div>

          <form @submit.prevent="handleLogin" class="login-form">
            <Input
              v-model="form.loginId"
              type="text"
              label="登入帳號"
              placeholder="請輸入電子信箱"
              :error="errors.loginId"
              required
              autocomplete="username"
            >
              <template #prefix><i class="fas fa-user"></i></template>
            </Input>

            <div class="password-input-wrapper">
              <Input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                label="密碼"
                placeholder="請輸入您的密碼"
                :error="errors.password"
                required
                autocomplete="current-password"
              >
                <template #prefix><i class="fas fa-lock"></i></template>
              </Input>
              <button 
                type="button" 
                class="toggle-password-btn"
                @click="showPassword = !showPassword"
                :aria-label="showPassword ? '隱藏密碼' : '顯示密碼'"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>

            <div class="form-options">
              <div class="form-check">
                <input 
                  id="remember" 
                  v-model="form.rememberMe" 
                  type="checkbox" 
                  class="form-check-input"
                />
                <label for="remember" class="form-check-label">
                  記住我
                </label>
              </div>
              
              <router-link to="/forgot-password" class="forgot-link">忘記密碼？</router-link>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              :loading="loading"
              block
            >
              <template #icon><i class="fas fa-sign-in-alt"></i></template>
              {{ loading ? '登入中...' : '登入' }}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { accountHomePath, safeLoginDestination, loginFormErrors } from '@/utils/authNavigation'
import Card from '@/components/Card.vue'
import Input from '@/components/Input.vue'
import Button from '@/components/Button.vue'

// State
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const loading = ref(false)
const showPassword = ref(false)

// Form data
const form = reactive({
  loginId: '',
  password: '',
  rememberMe: false
})

// Errors
const errors = ref<Record<string, string>>({})

// Methods
const validateForm = () => {
  errors.value = loginFormErrors(form.loginId, form.password)
  
  return Object.keys(errors.value).length === 0
}

const handleLogin = async () => {
  if (!validateForm()) return
  
  loading.value = true
  
  try {
    await userStore.signInWithLoginId(form.loginId.trim(), form.password)
    
    // Check if this is first login and requires password change
    if (userStore.userProfile?.isFirstLogin) {
      router.push('/change-password?firstLogin=true')
      return
    }
    
    // Redirect to intended page or dashboard
    await router.replace(safeLoginDestination(route.query.redirect, accountHomePath(userStore)))
    
  } catch (error: any) {
    console.error('Login error:', error)
    
    // Handle specific error messages
    if (error.code === 'auth/user-not-found') {
      errors.value.loginId = '找不到此帳號，請檢查電子信箱是否正確'
    } else if (error.code === 'auth/wrong-password') {
      errors.value.password = '密碼錯誤，請重新輸入'
    } else if (error.code === 'auth/invalid-email') {
      errors.value.loginId = '帳號格式不正確'
    } else if (error.code === 'auth/invalid-credential') {
      errors.value.general = '帳號或密碼錯誤，請重新確認'
    } else if (error.code === 'auth/too-many-requests') {
      errors.value.general = '登入嘗試次數過多，請稍後再試'
    } else if (error.code === 'auth/user-disabled') {
      errors.value.general = '此帳號已被停用，請聯繫管理員'
    } else {
      errors.value.general = '登入失敗，請稍後再試'
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.password-complete { display:flex; align-items:flex-start; gap:10px; padding:14px; margin-bottom:18px; border:1px solid var(--c-success); border-radius:8px; color:var(--c-success-fg); background:var(--c-success-bg); font-size:14px; line-height:1.6; }
.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, var(--c-primary-50) 0%, var(--c-secondary-50) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-4);
  position: relative;
  overflow: hidden;
}

.login-page::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -20%;
  width: 100%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  transform: rotate(-15deg);
}

.login-container {
  width: 100%;
  max-width: 400px;
  position: relative;
  z-index: 1;
}

.login-card {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

/* Logo Section */
.login-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: var(--sp-4);
  width: 100%;
}

.login-logo-img {
  height: 80px;
  width: auto;
  object-fit: contain;
  border-radius: var(--radius-lg);
  display: block;
}

/* Form Card */
.login-form-card {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.login-form-card:hover {
  transform: none !important;
}

.login-form-card :deep(.card-header) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.login-title {
  text-align: center;
  margin: 0;
  width: 100%;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.form-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-4);
}

.forgot-link {
  color: var(--c-primary);
  text-decoration: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  transition: color var(--transition-fast);
}

.forgot-link:hover {
  color: var(--c-primary-600);
  text-decoration: underline;
}


/* Password Input Wrapper */
.password-input-wrapper {
  position: relative;
}

.toggle-password-btn {
  position: absolute;
  right: var(--sp-3);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--c-gray-500);
  cursor: pointer;
  padding: var(--sp-2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color var(--transition-fast);
  z-index: 10;
  margin-top: 12px;
}

.toggle-password-btn:hover {
  color: var(--c-gray-700);
}

.toggle-password-btn:focus {
  outline: 2px solid var(--c-primary);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

/* Error Alert */
.error-alert {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  background-color: var(--c-danger-bg);
  border: 1px solid var(--c-danger);
  border-radius: var(--radius-md);
  color: var(--c-danger);
  font-size: var(--font-size-sm);
  margin-bottom: var(--sp-4);
}

.error-icon {
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

.error-message {
  flex: 1;
}

/* Responsive */
@media (max-width: 640px) {
  .login-page {
    padding: var(--sp-2);
  }
  
  .login-container {
    max-width: 100%;
  }
  
  .login-logo-img {
    height: 64px;
  }
  
  .form-options { gap: var(--sp-2); }
}
</style>
